// src/context/AuthContext.tsx - FIXED to use authAPI
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { authAPI } from '@/services/api'; // ✅ Use authAPI instead of fetch

/**
 * Authentication Context for Admin Panel
 * 
 * ✅ NOW USES authAPI which handles:
 * - Environment detection (dev vs production)
 * - Automatic JWT token attachment
 * - Proper error handling
 */

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  username: string | null;
  role: string | null;
  token: string | null;
  sessionExpiry: number | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  extendSession: () => Promise<{ success: boolean; message?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [sessionExpiry, setSessionExpiry] = useState<number | null>(null);

  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem('adminToken');
    const storedUsername = sessionStorage.getItem('adminUsername');
    const storedRole = sessionStorage.getItem('adminRole');

    if (storedToken && storedUsername && storedRole) {
      setToken(storedToken);
      setUsername(storedUsername);
      setRole(storedRole);
      setIsAuthenticated(true);
      
      // Start session monitoring
      checkSessionValidity();
    }
    
    setIsLoading(false);
  }, []);

  // Monitor session expiration every 10 seconds
  useEffect(() => {
    if (!token || !isAuthenticated) return;

    const checkSession = async () => {
      try {
        // ✅ Use authAPI instead of fetch
        const data = await authAPI.getSessionInfo();
        const remainingSeconds = data.remainingTimeSeconds || 0;
        setSessionExpiry(remainingSeconds);

        // Auto-logout when session expires
        if (remainingSeconds <= 0) {
          console.warn('⚠️ Session expired - logging out');
          
          toast({
            title: '⏱️ Session Expired',
            description: 'Your session has expired. Please login again.',
            variant: 'destructive',
          });
          
          logout();
        }
      } catch (error) {
        console.error('Session check error:', error);
        // Don't logout on network errors, only on 401s (handled by api.ts interceptor)
      }
    };

    // Check immediately
    checkSession();

    // Check every 10 seconds
    const interval = setInterval(checkSession, 10000);

    return () => clearInterval(interval);
  }, [token, isAuthenticated, toast]);

  const checkSessionValidity = async () => {
    try {
      // ✅ Use authAPI instead of fetch
      await authAPI.getSessionInfo();
    } catch (error) {
      console.warn('Invalid session detected - logging out');
      logout();
    }
  };

  const login = async (loginUsername: string, password: string) => {
    setIsLoading(true);
    
    try {
      // ✅ Use authAPI instead of fetch
      const data = await authAPI.login({
        username: loginUsername,
        password
      });

      const { token: authToken, username: user, role: userRole, expiresIn } = data;

      // Store in sessionStorage
      sessionStorage.setItem('adminToken', authToken);
      sessionStorage.setItem('adminUsername', user);
      sessionStorage.setItem('adminRole', userRole);

      // Update state
      setToken(authToken);
      setUsername(user);
      setRole(userRole);
      setIsAuthenticated(true);
      setSessionExpiry(expiresIn || 28800);

      console.log('✅ Login successful:', user);
      
      toast({
        title: '✅ Login Successful',
        description: `Welcome back, ${user}!`,
      });

      // Navigate to dashboard
      navigate('/admin/dashboard');
    } catch (error: any) {
      console.error('❌ Login failed:', error);
      throw new Error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all session data
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUsername');
    sessionStorage.removeItem('adminRole');

    // Reset state
    setToken(null);
    setUsername(null);
    setRole(null);
    setIsAuthenticated(false);
    setSessionExpiry(null);

    console.log('🔒 Logged out');
    
    // Navigate to login
    navigate('/admin');
  };

  const extendSession = async () => {
    if (!token) {
      return { success: false, message: 'No active session' };
    }

    try {
      // ✅ Use authAPI instead of fetch
      const data = await authAPI.extendSession();
      const newToken = data.token;

      if (newToken) {
        // Update token in storage and state
        sessionStorage.setItem('adminToken', newToken);
        setToken(newToken);
        setSessionExpiry(28800); // Reset to 8 hours

        console.log('✅ Session extended');
        
        toast({
          title: '✅ Session Extended',
          description: 'Your session has been extended by 8 hours',
        });
        
        return { success: true };
      }

      return { success: false, message: 'No token received' };
    } catch (error: any) {
      console.error('Failed to extend session:', error);
      return { success: false, message: error.message };
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    username,
    role,
    token,
    sessionExpiry,
    login,
    logout,
    extendSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}